// D6 — RealityAI Guide assistant (bottom-right) — the orchestrator.
//
// Responsibilities (all D6; the D5 backend stays authoritative):
//  * effective enablement: fetch D5 /api/guide/status (visible = system flag
//    AND user authorized AND user preference) + the persisted preference.
//    The robot is rendered ONLY when visible (S2/S3/S7: system OFF or pref
//    OFF ⇒ no robot, no panel, no timers, no Guide requests).
//  * state machine (renderer-independent) driven by app events; transient
//    states settle deterministically (GREETING→OPEN, SUCCESS→OPEN, …);
//  * lazy-load the robot + panel chunks (near-zero cost when disabled,
//    TechSpec §39) with a deterministic static fallback on asset/renderer
//    failure (TechSpec §38: no error ever escapes into the Chat tree);
//  * collision-safe bottom-right placement (does not cover the Chat composer;
//    mobile safe-area aware);
//  * accessibility: the trigger is a real <button> with the accessible name
//    "RealityAI Guide"; the decorative robot is aria-hidden; focus is returned
//    to the trigger on panel close (GuidePanel);
//  * reduced-motion: continuous/decorative motion disabled (TechSpec §30);
//  * cleanup: all timers + listeners are cleared on unmount / disable.
//
// The component receives a SAFE explicit UI context (surface/route/section/
// help_context_key) from the host (ChatPage) — it does NOT scrape the DOM to
// build context (TechSpec §21/§22).
//
// Settings sync: when the Settings UI (AccountPanel) flips the persisted D5
// preference it bumps `settingsTick`; the assistant re-reads the preference
// live so OFF hides the robot immediately and ON restores it (S4/S5/S6)
// without a logout.

import {
  Component,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { cn } from "@/lib/utils";
import {
  guideQuery,
  guideFeedback,
  guideStatus,
  fetchGuidePreference,
  GuideApiError,
} from "@/lib/guide/api";
import {
  createStateMachine,
  SLEEP_AFTER_MS,
} from "@/lib/guide/stateMachine";
import type {
  AssistantState,
  GuideContext,
  GuideQueryResponse,
} from "@/lib/guide/types";

export interface GuideAssistantProps {
  /** Safe explicit UI context (surface + optional route/section/key). */
  context: GuideContext;
  /** Called to open the full Documentation view, optionally to a slug. */
  onOpenDocumentation: (slug?: string) => void;
  /**
   * Bumped whenever the Settings UI changes the persisted Guide preference,
   * so the assistant re-reads it live (S4/S5) without a logout.
   */
  settingsTick?: number;
}

// --- contextual suggestions (safe explicit context only — no DOM scraping).
// Every entry maps to a REAL FAQ rule or troubleshooting trigger in
// docs/content/rule-data/ (D5, link-validated). Nothing unsupported is
// suggested; the D5 backend remains the authority on what answers exist.
const WEB_SUGGESTIONS = [
  "Что такое RealityAI?",
  "Как создать новый проект?",
  "Почему модель недоступна?",
  "Почему я не вижу агенты?",
  "Как войти?",
];
const RCC_SUGGESTIONS = [
  "Что такое RealityAI Guide?",
  "Как изменить тариф?",
  "Не могу войти",
  "Где посмотреть отчёты?",
];

/** Deterministic RU error messages per D5 error kind. */
function errorMessage(e: GuideApiError): string {
  switch (e.kind) {
    case "rate_limited":
      return "Слишком много запросов. Попробуйте через минуту.";
    case "validation":
      return "Не удалось разобрать запрос. Попробуйте сформулировать иначе.";
    case "disabled":
      return "RealityAI Guide сейчас отключён.";
    case "auth":
      return "Сессия истекла. Войдите заново.";
    default:
      return "Не удалось получить ответ. Проверьте соединение и попробуйте ещё раз.";
  }
}

/**
 * Static fallback for the renderer (renderer-failure fail-safe, TechSpec §38):
 * if the SVG/CSS robot chunk fails to load or throws at runtime, a minimal
 * STATIC robot (silhouette + visor + cyan eyes + glowing "R" core) is shown
 * instead — the assistant keeps its RealityAI identity (never an orb or a
 * generic support icon), stays usable, the panel still opens, and no error
 * escapes into the Chat tree.
 *
 * Fully static (no animation) so it is inherently reduced-motion safe.
 */
function StaticRobotFallback({ size = 64 }: { size?: number }) {
  return (
    <div aria-hidden style={{ width: size, height: size }}>
      <svg
        width={size}
        height={size}
        viewBox="0 0 96 96"
        aria-hidden="true"
        style={{ display: "block", overflow: "visible" }}
      >
        <defs>
          <linearGradient id="ra-fb-body" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#F8FAFC" />
            <stop offset="100%" stopColor="#CBD5E1" />
          </linearGradient>
          <linearGradient id="ra-fb-visor" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#1F2937" />
            <stop offset="100%" stopColor="#0B1220" />
          </linearGradient>
          <linearGradient id="ra-fb-core" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#111827" />
            <stop offset="100%" stopColor="#0B1220" />
          </linearGradient>
        </defs>
        {/* body */}
        <rect x="28" y="66" width="40" height="22" rx="9" fill="url(#ra-fb-body)" stroke="#94A3B8" strokeWidth="1" />
        {/* glowing R core */}
        <circle cx="48" cy="77" r="8" fill="rgba(34,211,238,0.16)" />
        <circle cx="48" cy="77" r="6.6" fill="url(#ra-fb-core)" stroke="#22D3EE" strokeOpacity="0.6" strokeWidth="1" />
        <text
          x="48"
          y="80.5"
          textAnchor="middle"
          fontSize="11"
          fontFamily="var(--font-serif, 'Instrument Serif', Georgia, serif)"
          fontWeight={600}
          fill="#22D3EE"
        >
          R
        </text>
        {/* head */}
        <line x1="48" y1="22" x2="48" y2="13" stroke="#38BDF8" strokeWidth="2.5" strokeLinecap="round" />
        <circle cx="48" cy="11" r="3" fill="#22D3EE" />
        <rect x="18" y="22" width="60" height="44" rx="15" fill="url(#ra-fb-body)" stroke="#94A3B8" strokeWidth="1" />
        <rect x="14" y="38" width="6" height="16" rx="3" fill="#38BDF8" />
        <rect x="76" y="38" width="6" height="16" rx="3" fill="#38BDF8" />
        <rect x="26" y="32" width="44" height="24" rx="12" fill="url(#ra-fb-visor)" />
        {/* cyan eyes */}
        <circle cx="38" cy="44" r="4" fill="#22D3EE" />
        <circle cx="58" cy="44" r="4" fill="#22D3EE" />
      </svg>
    </div>
  );
}

class RendererErrorBoundary extends Component<
  { children: ReactNode; fallback: ReactNode },
  { failed: boolean }
> {
  state = { failed: false };
  static getDerivedStateFromError() {
    return { failed: true };
  }
  componentDidCatch() {
    // Intentionally silent: a renderer failure must never break the Chat tree.
  }
  render() {
    return this.state.failed ? this.props.fallback : this.props.children;
  }
}

interface RobotRendererProps {
  state: AssistantState;
  size: number;
  reducedMotion: boolean;
}

/**
 * Renderer boundary for the SVG/CSS robot (the D6 primary renderer,
 * TechSpec §27). Loads the robot chunk on demand (lazy — near-zero cost when
 * the Guide is disabled) and deterministically falls back to the static "R"
 * badge if the chunk fails to load or the component throws (asset failure ⇒
 * static fallback, TechSpec §38). A future Rive/WebP renderer only has to
 * provide a different `AssistantRobot` implementation — the state controller
// is renderer-independent.
 */
function RobotRenderer(props: RobotRendererProps) {
  const [mod, setMod] = useState<{
    AssistantRobot: (p: RobotRendererProps & { animate?: boolean }) => ReactNode;
  } | null>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let alive = true;
    import("./AssistantRobot")
      .then((m) => {
        if (alive) setMod({ AssistantRobot: m.AssistantRobot });
      })
      .catch(() => {
        if (alive) setFailed(true);
      });
    return () => {
      alive = false;
    };
  }, []);

  if (failed) return <StaticRobotFallback size={props.size} />;
  if (!mod) {
    return (
      <div
        aria-hidden
        style={{ width: props.size, height: props.size }}
        className="rounded-full border border-hairline bg-app/70"
      />
    );
  }
  const Robot = mod.AssistantRobot;
  return (
    <RendererErrorBoundary fallback={<StaticRobotFallback size={props.size} />}>
      <Robot
        state={props.state}
        size={props.size}
        reducedMotion={props.reducedMotion}
        animate
      />
    </RendererErrorBoundary>
  );
}

interface PanelRendererProps {
  open: boolean;
  onClose: () => void;
  state: AssistantState;
  answer: GuideQueryResponse | null;
  error: string | null;
  suggestions: string[];
  onAsk: (question: string) => void;
  onFeedback: (helpful: boolean) => void;
  onOpenDocumentation: (slug?: string) => void;
  reducedMotion: boolean;
  surface?: "web" | "rcc";
}

/** Lazy panel: the chunk loads only when the Guide is visible (TechSpec §39). */
function PanelRenderer(props: PanelRendererProps) {
  const [mod, setMod] = useState<{
    GuidePanel: (p: PanelRendererProps) => ReactNode;
  } | null>(null);
  useEffect(() => {
    let alive = true;
    import("./GuidePanel")
      .then((m) => {
        if (alive) setMod({ GuidePanel: m.GuidePanel });
      })
      .catch(() => {
        /* panel chunk failure: the assistant stays visible, panel simply
           does not open — core Chat unaffected (TechSpec §38). */
      });
    return () => {
      alive = false;
    };
  }, []);
  if (!mod || !props.open) return null;
  const Panel = mod.GuidePanel;
  return <Panel {...props} />;
}

export function GuideAssistant({
  context,
  onOpenDocumentation,
  settingsTick = 0,
}: GuideAssistantProps) {
  const [status, setStatus] = useState<{ visible: boolean; system_enabled: boolean; user_preference: boolean } | null>(null);
  const [pref, setPref] = useState<boolean | null>(null);
  const [panelOpen, setPanelOpen] = useState(false);
  const [answer, setAnswer] = useState<GuideQueryResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [hover, setHover] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);

  const machineRef = useRef(createStateMachine("IDLE"));
  const [state, setState] = useState<AssistantState>(machineRef.current.state);
  const sleepTimer = useRef<number | null>(null);
  const settleTimer = useRef<number | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);

  const send = useCallback(
    (ev: Parameters<typeof machineRef.current.send>[0]) => {
      const s = machineRef.current.send(ev);
      setState(s);
    },
    [],
  );

  // Schedule the deterministic settle of a transient state (GREETING/FOUND/
  // SUCCESS/CONFUSED → OPEN). Fixed short durations; never synced to token
  // streams. Cleared on any new event / panel close / unmount.
  const scheduleSettle = useCallback(
    (ms: number) => {
      if (settleTimer.current) window.clearTimeout(settleTimer.current);
      settleTimer.current = window.setTimeout(() => send({ type: "SETTLE" }), ms);
    },
    [send],
  );

  const clearSettle = useCallback(() => {
    if (settleTimer.current) {
      window.clearTimeout(settleTimer.current);
      settleTimer.current = null;
    }
  }, []);

  // prefers-reduced-motion (TechSpec §30).
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setReducedMotion(mq.matches);
    update();
    mq.addEventListener?.("change", update);
    return () => mq.removeEventListener?.("change", update);
  }, []);

  // Effective enablement: D5 status (system flag AND authorized AND pref) +
  // the persisted preference. Fetched on mount AND re-fetched whenever the
  // Settings UI changes the preference (settingsTick) so OFF hides the robot
  // immediately and ON restores it (S4/S5) without a logout. When NOT visible
  // we render nothing (no robot, no timers, no further Guide requests).
  useEffect(() => {
    let alive = true;
    const ctrl = new AbortController();
    Promise.allSettled([guideStatus(ctrl.signal), fetchGuidePreference(ctrl.signal)])
      .then(([s, p]) => {
        if (!alive) return;
        if (s.status === "fulfilled") setStatus(s.value);
        if (p.status === "fulfilled") setPref(p.value);
      })
      .finally(() => ctrl.abort());
    return () => {
      alive = false;
      ctrl.abort();
    };
  }, [settingsTick]);

  const visible = (status?.visible ?? false) && (pref ?? true);

  // SLEEP: enter after long inactivity from a settled state; clear on any
  // activity. Only active while visible + panel closed (idle).
  const scheduleSleep = useCallback(() => {
    if (sleepTimer.current) window.clearTimeout(sleepTimer.current);
    if (!visible) return;
    sleepTimer.current = window.setTimeout(() => {
      send({ type: "IDLE_TIMEOUT" });
    }, SLEEP_AFTER_MS);
  }, [visible, send]);

  const clearSleep = useCallback(() => {
    if (sleepTimer.current) {
      window.clearTimeout(sleepTimer.current);
      sleepTimer.current = null;
    }
  }, []);

  // When the Guide becomes invisible, tear down all activity (S4/S7):
  // panel closes, in-flight request aborts, all timers stop, no Guide
  // background queries are initiated.
  useEffect(() => {
    if (!visible) {
      clearSleep();
      clearSettle();
      abortRef.current?.abort();
      abortRef.current = null;
      setPanelOpen(false);
      setAnswer(null);
      setError(null);
    }
  }, [visible, clearSleep, clearSettle]);

  // Cleanup on unmount: abort any in-flight request + clear all timers.
  useEffect(() => {
    return () => {
      clearSleep();
      clearSettle();
      abortRef.current?.abort();
    };
  }, [clearSleep, clearSettle]);

  const handleOpen = useCallback(() => {
    clearSleep();
    clearSettle();
    send({ type: "USER_INTERACT" });
    send({ type: "PANEL_OPEN" }); // -> GREETING
    scheduleSettle(1400); // GREETING -> OPEN
    setAnswer(null);
    setError(null);
    setPanelOpen(true);
  }, [send, clearSleep, clearSettle, scheduleSettle]);

  const handleClose = useCallback(() => {
    setPanelOpen(false);
    clearSettle();
    send({ type: "PANEL_CLOSE" }); // -> IDLE
    scheduleSleep();
    // Return focus to the trigger (focus management, no trap).
    window.setTimeout(() => triggerRef.current?.focus(), 0);
  }, [send, clearSettle, scheduleSleep]);

  const handleAsk = useCallback(
    (question: string) => {
      clearSleep();
      clearSettle();
      setError(null);
      setAnswer(null);
      send({ type: "QUESTION_SUBMITTED" }); // -> THINKING
      abortRef.current?.abort();
      const ctrl = new AbortController();
      abortRef.current = ctrl;

      guideQuery(question, { context, signal: ctrl.signal })
        .then((res) => {
          setAnswer(res);
          if (res.no_result) {
            send({ type: "NO_RESULT" }); // -> NO_RESULTS
          } else {
            send({ type: "RESULT", result_type: res.result_type }); // -> FOUND
            scheduleSettle(900); // FOUND -> OPEN (answer is painted)
          }
        })
        .catch((e: unknown) => {
          if (e instanceof DOMException && e.name === "AbortError") return;
          const ge = e as GuideApiError;
          if (ge?.kind === "disabled") {
            // Guide turned off mid-flight — hide everything.
            setStatus((prev) => (prev ? { ...prev, visible: false } : prev));
            return;
          }
          setError(errorMessage(ge));
          send({ type: "ERROR" });
        })
        .finally(() => {
          if (abortRef.current === ctrl) abortRef.current = null;
        });
    },
    [context, send, clearSleep, clearSettle, scheduleSettle],
  );

  const handleFeedback = useCallback(
    (helpful: boolean) => {
      if (!answer) return;
      // Fire-and-forget with graceful error (must not break the Guide).
      guideFeedback(answer.query_id, helpful)
        .then(() => send({ type: "FEEDBACK_SENT", helpful }))
        .catch(() => {
          // Silent failure (TechSpec §38): feedback failure never breaks the
          // answer; the visual state still reflects the user's choice.
          send({ type: "FEEDBACK_SENT", helpful });
        });
      scheduleSettle(1800); // SUCCESS/CONFUSED -> OPEN
    },
    [answer, send, scheduleSettle],
  );

  // Suggestions: context-aware per surface (safe explicit context only —
  // no DOM scraping). Every question maps to a real D5 rule (FAQ/troubles-
  // hooting corpus) — no unsupported product behavior is suggested.
  const suggestions = useMemo(() => {
    if (context.surface === "rcc") return RCC_SUGGESTIONS;
    return WEB_SUGGESTIONS;
  }, [context.surface]);

  const handleTriggerClick = useCallback(() => {
    if (panelOpen) handleClose();
    else handleOpen();
  }, [panelOpen, handleOpen, handleClose]);

  // Not visible ⇒ render NOTHING (no robot, no panel, no timers, no requests).
  if (!visible) return null;

  const displayState: AssistantState = panelOpen ? (state === "IDLE" ? "OPEN" : state) : state;

  return (
    <div
      className="guide-assistant fixed z-40 right-5 bottom-[calc(20px+env(safe-area-inset-bottom))] md:right-6 md:bottom-6"
      data-guide-assistant
    >
      {/* lazy: robot only mounts once the Guide is confirmed visible */}
      <button
        ref={triggerRef}
        type="button"
        onClick={handleTriggerClick}
        onMouseEnter={() => {
          setHover(true);
          clearSleep();
          if (machineRef.current.state === "SLEEP") send({ type: "USER_INTERACT" });
        }}
        onMouseLeave={() => {
          setHover(false);
          scheduleSleep();
        }}
        onFocus={() => clearSleep()}
        onBlur={() => scheduleSleep()}
        className={cn(
          "relative rounded-full border border-hairline bg-app/80 backdrop-blur shadow-xl",
          "p-1.5 md:p-2 transition-transform hover:scale-[1.03] focus:outline-none focus-visible:ring-2 focus-visible:ring-accent",
          "w-16 h-16 md:w-20 md:h-20"
        )}
        aria-label="RealityAI Guide"
        aria-expanded={panelOpen}
        aria-haspopup="dialog"
      >
        <RobotRenderer
          state={hover && !panelOpen ? "HOVER" : displayState}
          size={64}
          reducedMotion={reducedMotion}
        />
      </button>

      <PanelRenderer
        open={panelOpen}
        onClose={handleClose}
        state={displayState}
        answer={answer}
        error={error}
        suggestions={suggestions}
        onAsk={handleAsk}
        onFeedback={handleFeedback}
        onOpenDocumentation={onOpenDocumentation}
        reducedMotion={reducedMotion}
        surface={context.surface === "rcc" ? "rcc" : "web"}
      />
    </div>
  );
}

// Re-exports for the Settings toggle (AccountPanel) — D5 preference key.
export { setGuidePreference, fetchGuidePreference } from "@/lib/guide/api";
export { GUIDE_PREF_KEY } from "@/lib/guide/api";
